import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { chartOfAccountsApi } from '../api/chartOfAccountsApi';
import { 
  ChartOfAccount, 
  ChartOfAccountFilterParams, 
  ChartOfAccountFormData,
  AccountType 
} from '../types/chart-of-accounts.types';

export const useChartOfAccounts = (params?: ChartOfAccountFilterParams) => {
  const queryClient = useQueryClient();

  const {
    data: chartOfAccounts = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['chartOfAccounts', params],
    queryFn: () => chartOfAccountsApi.getChartOfAccounts(params || {}),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    chartOfAccounts,
    isLoading,
    error,
    refetch
  };
};

export const useChartOfAccount = (id: string) => {
  const {
    data: chartOfAccount,
    isLoading,
    error
  } = useQuery({
    queryKey: ['chartOfAccount', id],
    queryFn: () => chartOfAccountsApi.getChartOfAccountById(id),
    enabled: !!id,
  });

  return {
    chartOfAccount,
    isLoading,
    error
  };
};

export const useCreateChartOfAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ChartOfAccountFormData) => chartOfAccountsApi.createChartOfAccount(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chartOfAccounts'] });
    },
  });
};

export const useUpdateChartOfAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ChartOfAccountFormData> }) =>
      chartOfAccountsApi.updateChartOfAccount(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['chartOfAccounts'] });
      queryClient.invalidateQueries({ queryKey: ['chartOfAccount', variables.id] });
    },
  });
};

export const useDeleteChartOfAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => chartOfAccountsApi.deleteChartOfAccount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chartOfAccounts'] });
    },
  });
};

export const useUpdateChartOfAccountStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      chartOfAccountsApi.updateChartOfAccountStatus(id, isActive),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['chartOfAccounts'] });
      queryClient.invalidateQueries({ queryKey: ['chartOfAccount', variables.id] });
    },
  });
};

export const useSearchChartOfAccounts = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const {
    data: searchResults = [],
    isLoading: isSearching,
    error: searchError
  } = useQuery({
    queryKey: ['searchChartOfAccounts', debouncedSearchTerm],
    queryFn: () => chartOfAccountsApi.searchChartOfAccounts(debouncedSearchTerm),
    enabled: debouncedSearchTerm.length > 0,
  });

  return {
    searchTerm,
    setSearchTerm,
    searchResults,
    isSearching,
    searchError
  };
};
